import type { ChangeEvent, FC, KeyboardEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import colors from 'app/styles/common/colors.module.scss';

import { useContacts } from 'hooks/useContacts';
import { getTestIdByParts } from 'utils/getTestIdByParts';
import type { WithTestId } from 'types/app';

import { AnalyticsTypes } from '@Modules/analytics/types';
import { analytics } from '@Modules/analytics/analytics';

import { Button } from '@Atomic/atoms/Button';
import { Icons } from '@Atomic/atoms/Icons';
import { Input } from '@Atomic/atoms/Input';
import { Tags } from '@Atomic/atoms/Tags';
import type { TagOption } from '@Atomic/atoms/Tags/types';

import styles from './style.module.scss';
import type { AskSeller, AskSellerQuestion } from './types';

type AskOfferSellerProps = WithTestId<{
  offerId: number;
  askSeller: AskSeller;
}>;

export const AskOfferSeller: FC<AskOfferSellerProps> = ({
  offerId,
  askSeller,
  testId = 'ask-offer-seller',
}) => {
  const { t } = useTranslation();
  const { openChat } = useContacts(offerId);

  const { questions, startPlaceholder } = askSeller;
  const initialQuestion: AskSellerQuestion | undefined =
    questions.find((question) => question.selected) ?? questions[0];

  const [selectedQuestion, setSelectedQuestion] = useState(initialQuestion);
  // For askSeller the backend duplicates `placeholder = value` as the complete
  // message (greeting included), so the input shows the placeholder directly.
  // Fallback chain matches iOS: selected placeholder → startPlaceholder → ''.
  const [message, setMessage] = useState(initialQuestion?.placeholder || startPlaceholder || '');

  // Defensive: the sole caller already gates on a non-empty `questions` array,
  // but never assume it — an empty array would otherwise crash on `.id`.
  if (!selectedQuestion) {
    return null;
  }

  const options: TagOption[] = questions.map((question, index) => ({
    label: question.title,
    value: String(question.id),
    testId: getTestIdByParts(testId, { value: 'tag', index }),
  }));

  // Every actionType is deliberately treated as PREFILL on web: the backend
  // sends PREFILL-only questions for askSeller today, and this block has no
  // chat-draft affordance (divergence by design from Android, which routes
  // OPEN_CHAT to the chat draft).
  const handleTagChange = (value: string | string[]): void => {
    if (Array.isArray(value)) {
      return;
    }

    const question = questions.find((item) => String(item.id) === value);

    if (!question) {
      return;
    }

    setSelectedQuestion(question);
    // Same fallback chain as the mount state: `placeholder` is a plain string
    // in the schema, so an empty one must not leave the input blank with a
    // dead send button.
    setMessage(question.placeholder || startPlaceholder || '');
  };

  const handleMessageChange = (e: ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);
  };

  // Sending must not reset the block: the chosen chip stays highlighted and
  // the input keeps its message (app parity, BP-7737).
  const handleOpenChat = () => {
    // A cleared input is a designed no-op (mirrors OfferBid) — don't open a
    // chat that would send nothing or fire send analytics.
    if (!message.trim()) {
      return;
    }

    // Cross-platform click event (BP-7402), fired on the raw press even for
    // auth-gated anonymous users.
    analytics.offerClickSendQuestion({
      offer_id: offerId,
      question_id: selectedQuestion.id,
      placement: AnalyticsTypes.OfferQuestionPlacement.ASK_SELLER,
    });

    openChat({ message });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleOpenChat();
    }
  };

  return (
    <div className={styles.container} data-testid={testId}>
      <div className={styles.inputContainer}>
        <Input
          value={message}
          onChange={handleMessageChange}
          className={styles.input}
          onKeyDown={handleKeyDown}
          testId={getTestIdByParts(testId, 'input')}
          placeholder={selectedQuestion.placeholder || startPlaceholder || undefined}
          aria-label={t('offer.askSeller.inputLabel')}
        />

        <Button
          intent="accent"
          presentation="filled"
          size="small"
          circle
          iconLeft={<Icons.SendFill width={20} height={20} color={colors.white} />}
          onClick={handleOpenChat}
          testId="ask-offer-seller-send"
          aria-label={t('offer.askSeller.sendLabel')}
        />
      </div>

      <Tags
        options={options}
        color="grey"
        size="medium"
        className={styles.tags}
        multiple={false}
        allowEmptySelection={false}
        value={String(selectedQuestion.id)}
        onChange={handleTagChange}
        testId={getTestIdByParts(testId, 'tags')}
        aria-label={t('offer.askSeller.optionsLabel')}
      />
    </div>
  );
};
